import { FC, ReactNode } from 'react';
import { resolveAgentRoute } from './agentRouteMap';

/**
 * Render de los mensajes del chat de Paulina — extraído de AgentChatWidget para
 * que el panel desktop (PaulinaPanel) y el bottom-sheet móvil (PaulinaSheet)
 * reusen EXACTAMENTE el mismo molde sin duplicar la lógica de parseo. Mantiene el
 * contrato de navegación por llave (ACCION_IR → resolveAgentRoute), el botón de
 * soporte (ACCION_SOPORTE), imágenes, links markdown/sueltos y **negritas**.
 *
 * Render enriquecido: además de párrafos, agrupa líneas consecutivas en BLOQUES y
 * pinta listas (viñetas / numeradas), tablas markdown (con scroll horizontal para
 * móvil) y callouts (⚠️ ✅ ❌ 💡 ℹ️). El parseo es presentacional y robusto: si un
 * patrón no aplica, la línea cae a párrafo normal. Todo debe verse bien en desktop
 * Y en el bottom-sheet móvil (~92vw), por eso las tablas viven en un contenedor
 * `overflow-x-auto` y nada usa anchos fijos que desborden.
 */

const IMAGE_URL = /^(?:captura:\s*)?(https?:\/\/\S+\.(?:jpe?g|png|webp))\s*$/i;
const NAV_LINE = /^ACCION_IR:(\w+)$/;
const SOPORTE_LINE = /^ACCION_SOPORTE:(https?:\/\/\S+)$/;
const MD_IMAGE = /!\[[^\]]*\]\(\s*(https?:\/\/[^)\s]+)\s*\)/g;
const MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
const LINK = /(https?:\/\/[^\s)]+)/g;

// Patrones de bloque (se evalúan sobre la línea ya trimmeada).
const BULLET = /^[-*]\s+(.*)$/;
const NUMBERED = /^(\d+)\.\s+(.*)$/;
const TABLE_ROW = /^\|(.+)\|\s*$/;
// Fila separadora de tabla markdown: |---|:--:|---| (guiones y dos puntos de alineación).
const TABLE_SEP = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/;
// Header markdown: 1–6 almohadillas seguidas de un espacio y texto (# Título … ###### Título).
const HEADER = /^(#{1,6})\s+(.*)$/;
// Regla horizontal: línea que es SOLO 3+ guiones/asteriscos/guiones bajos. Sin
// pipes (eso es separadora de tabla) y sin más caracteres (eso es texto).
const HR = /^(?:-{3,}|\*{3,}|_{3,})$/;
// Callouts: la línea empieza con uno de estos emojis. Cada uno mapea a un tono.
const CALLOUT_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  '⚠️': { bg: '#fffbeb', border: '#fcd34d', text: '#92400e' }, // ámbar
  '✅': { bg: '#f0fdf4', border: '#86efac', text: '#166534' }, // verde
  '❌': { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b' }, // rojo
  '💡': { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af' }, // azul
  ℹ️: { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af' }, // azul
};
const CALLOUT_EMOJIS = Object.keys(CALLOUT_STYLES);

const linkClass = 'break-all text-[#4298B5] underline';
const codeClass = 'rounded bg-[#f1f4f6] px-1 py-0.5 font-mono text-[0.85em]';

const renderLinks = (text: string, key: string) =>
  // Test stateless: LINK es global (lastIndex stateful); .test() en bucle falla
  // intermitentemente. Los fragmentos capturados por split que son URL empiezan con http(s).
  text.split(LINK).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={`${key}-${i}`} href={part} target="_blank" rel="noreferrer" className={linkClass}>
        {part}
      </a>
    ) : (
      part
    ),
  );

// Inline code `texto`: lo que va entre un par de backticks es literal (no se
// reparsea por negritas/links). Los fragmentos impares del split son el código;
// los pares siguen el flujo normal de URLs sueltas.
const renderCode = (text: string, key: string): ReactNode[] =>
  text.split(/`([^`]+)`/g).map((part, i) =>
    i % 2 === 1 ? (
      <code key={`${key}-c${i}`} className={codeClass}>
        {part}
      </code>
    ) : (
      renderLinks(part, `${key}-${i}`)
    ),
  );

// Texto inline: links markdown [texto](url), URLs sueltas y **negritas**.
const renderInline = (text: string, key: string): ReactNode[] => {
  const parts = text.split(MD_LINK); // [texto, label, url, texto, ...]
  const out: ReactNode[] = [];
  for (let i = 0; i < parts.length; i += 3) {
    const plain = parts[i] ?? '';
    out.push(
      ...plain
        .split(/\*\*([^*]+)\*\*/g)
        .map((p, j) =>
          j % 2 === 1 ? <strong key={`${key}-b${i}-${j}`}>{p}</strong> : renderCode(p, `${key}-${i}-${j}`),
        ),
    );
    if (i + 2 < parts.length) {
      out.push(
        <a key={`${key}-l${i}`} href={parts[i + 2]} target="_blank" rel="noreferrer" className={linkClass}>
          {parts[i + 1]}
        </a>,
      );
    }
  }
  return out;
};

// ¿La línea (trimmeada) empieza con un emoji de callout? Devuelve el emoji o null.
const matchCallout = (line: string): string | null => CALLOUT_EMOJIS.find((e) => line.startsWith(e)) ?? null;

// Divide la fila `| a | b |` en celdas, descartando los pipes de borde.
const splitRow = (line: string): string[] =>
  line
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

// Renderiza un bloque de tabla markdown. `rows` son las líneas crudas (sin la
// separadora). La primera fila es el header. Envuelto en overflow-x-auto para que
// en el sheet móvil (~92vw) haga scroll horizontal en vez de romper el layout.
const renderTable = (rows: string[], key: string) => {
  const [head, ...body] = rows;
  const headCells = splitRow(head);
  return (
    <div key={key} className="my-1.5 overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {headCells.map((cell, c) => (
              <th
                key={c}
                className="border border-[#e2e8f0] bg-[#f1f4f6] px-2 py-1 text-left font-semibold text-[#253746]"
              >
                {renderInline(cell, `${key}-h${c}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => {
            const cells = splitRow(row);
            return (
              <tr key={r}>
                {headCells.map((_, c) => (
                  <td key={c} className="border border-[#e2e8f0] px-2 py-1 align-top text-[#253746]">
                    {renderInline(cells[c] ?? '', `${key}-r${r}-${c}`)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Render ligero del mensaje: imágenes, links, negritas, botón de navegación, y
// además listas / tablas / callouts agrupando líneas consecutivas en bloques.
export const MessageContent: FC<{ text: string; onNavigate: (seccion: string) => void }> = ({ text, onNavigate }) => {
  // Las imágenes markdown ![alt](url) se separan a su propia línea como URL.
  const normalized = text.replace(MD_IMAGE, '\n$1\n');
  const lines = normalized.split('\n');
  const out: ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trim();

    // --- Línea especial: botón de soporte ---
    const sop = line.match(SOPORTE_LINE);
    if (sop) {
      out.push(
        <a
          key={i}
          href={sop[1]}
          target="_blank"
          rel="noreferrer"
          className="my-1 flex w-full items-center justify-center gap-2 rounded-lg bg-[#25D366] px-3 py-2 text-center text-sm font-semibold text-[#ffffff]"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.5A10 10 0 1 0 12 2zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-2.9.9.9-2.8-.2-.3A8 8 0 1 1 12 20zm4.4-5.6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1-.6.8-.8 1-.3.2-.5.1a6.6 6.6 0 0 1-1.9-1.2 7.3 7.3 0 0 1-1.4-1.7c-.1-.2 0-.4.1-.5l.4-.4.2-.4a.5.5 0 0 0 0-.5l-.8-1.9c-.2-.5-.4-.4-.5-.4h-.5a.9.9 0 0 0-.7.3 2.8 2.8 0 0 0-.9 2.1 4.9 4.9 0 0 0 1 2.6 11 11 0 0 0 4.3 3.8c.6.3 1.1.4 1.5.5a3.5 3.5 0 0 0 1.6.1c.5-.1 1.4-.6 1.6-1.1s.2-1 .1-1.1z" />
          </svg>
          Hablar con soporte →
        </a>,
      );
      continue;
    }

    // --- Línea especial: botón de navegación ---
    const nav = line.match(NAV_LINE);
    if (nav) {
      const seccion = nav[1];
      if (!resolveAgentRoute(seccion)) continue; // llave desconocida → sin botón roto
      out.push(
        <button
          key={i}
          type="button"
          onClick={() => onNavigate(seccion)}
          className="my-1 w-full rounded-lg bg-[#4298B5] px-3 py-2 text-left text-sm font-semibold text-[#ffffff]"
        >
          Llévame a {seccion.replace(/_/g, ' ')} →
        </button>,
      );
      continue;
    }

    // --- Línea especial: imagen ---
    const img = line.match(IMAGE_URL);
    if (img) {
      out.push(
        <img
          key={i}
          src={img[1]}
          alt="Captura del panel"
          loading="lazy"
          className="my-1 w-full rounded-lg border border-[#e2e8f0]"
        />,
      );
      continue;
    }

    // --- Regla horizontal: --- / *** / ___ (3+). Sin pipes → no es separadora
    // de tabla; la tabla se detecta aparte por su header + |---| previo. ---
    if (HR.test(line)) {
      out.push(<hr key={`hr${i}`} className="my-2 border-0 border-t border-[#e2e8f0]" />);
      continue;
    }

    // --- Header markdown (# … ######) a escala de chat. # / ## más grande que
    // ### en adelante. Se quitan las almohadillas; el resto sigue siendo inline. ---
    const header = line.match(HEADER);
    if (header) {
      const level = header[1].length;
      const headerClass = level <= 2 ? 'text-base font-bold' : 'text-sm font-semibold';
      out.push(
        <p key={`h${i}`} className={`my-1.5 break-words text-[#253746] ${headerClass}`}>
          {renderInline(header[2], `h${i}`)}
        </p>,
      );
      continue;
    }

    // --- Bloque: tabla markdown (header + separadora |---| + filas) ---
    if (TABLE_ROW.test(line) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1].trim())) {
      const rows = [line];
      let j = i + 2; // saltamos header (i) y separadora (i+1)
      while (j < lines.length && TABLE_ROW.test(lines[j].trim())) {
        rows.push(lines[j].trim());
        j++;
      }
      out.push(renderTable(rows, `t${i}`));
      i = j - 1;
      continue;
    }

    // --- Bloque: lista con viñetas (- / *) ---
    if (BULLET.test(line)) {
      const items: string[] = [];
      let j = i;
      while (j < lines.length) {
        const m = lines[j].trim().match(BULLET);
        if (!m) break;
        items.push(m[1]);
        j++;
      }
      out.push(
        <ul key={`ul${i}`} className="my-1.5 list-disc space-y-0.5 pl-5">
          {items.map((item, k) => (
            <li key={k} className="break-words">
              {renderInline(item, `ul${i}-${k}`)}
            </li>
          ))}
        </ul>,
      );
      i = j - 1;
      continue;
    }

    // --- Bloque: lista numerada (1. 2. 3.) ---
    if (NUMBERED.test(line)) {
      const items: string[] = [];
      let j = i;
      const start = Number(line.match(NUMBERED)?.[1] ?? 1);
      while (j < lines.length) {
        const m = lines[j].trim().match(NUMBERED);
        if (!m) break;
        items.push(m[2]);
        j++;
      }
      out.push(
        <ol key={`ol${i}`} start={start} className="my-1.5 list-decimal space-y-0.5 pl-5">
          {items.map((item, k) => (
            <li key={k} className="break-words">
              {renderInline(item, `ol${i}-${k}`)}
            </li>
          ))}
        </ol>,
      );
      i = j - 1;
      continue;
    }

    // --- Bloque: callout (⚠️ ✅ ❌ 💡 ℹ️) ---
    const callout = matchCallout(line);
    if (callout) {
      const style = CALLOUT_STYLES[callout];
      out.push(
        <div
          key={`c${i}`}
          className="my-1.5 rounded-lg border px-3 py-2 text-sm break-words"
          style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text }}
        >
          {renderInline(line, `c${i}`)}
        </div>,
      );
      continue;
    }

    // --- Por defecto: párrafo normal ---
    out.push(
      <p key={i} className="my-1.5 min-h-[0.5rem] whitespace-pre-wrap break-words">
        {renderInline(raw, `${i}`)}
      </p>,
    );
  }

  return <>{out}</>;
};
